import { useEffect, useRef } from "react";

const REDUCED = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Abstract financial-market canvas: subtle grid, drifting candlestick fragments,
// cursor-reactive price lines and mono tick readouts.
const MarketCanvas = ({ className = "", dark = false }) => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h, dpr;
    const candles = [];
    const ticks = [];

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const seed = () => {
      candles.length = 0;
      ticks.length = 0;
      const n = Math.max(10, Math.floor(w / 90));
      for (let i = 0; i < n; i++) {
        candles.push({
          x: (i + 0.5) * (w / n) + (Math.random() - 0.5) * 20,
          y: Math.random() * h,
          bh: 24 + Math.random() * 60,
          bw: 5 + Math.random() * 5,
          speed: 0.06 + Math.random() * 0.12,
          phase: Math.random() * Math.PI * 2,
          up: Math.random() > 0.5,
          layer: 0.4 + Math.random() * 0.6,
        });
      }
      for (let i = 0; i < 7; i++) {
        ticks.push({
          x: Math.random() * w,
          y: Math.random() * h,
          val: (18000 + Math.random() * 6000).toFixed(2),
          phase: Math.random() * Math.PI * 2,
        });
      }
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current.tx = (e.clientX - rect.left) / rect.width;
      mouse.current.ty = (e.clientY - rect.top) / rect.height;
    };

    const line = dark ? "rgba(247,247,244,0.06)" : "rgba(16,16,16,0.05)";
    const green = dark ? "rgba(39,107,85,0.75)" : "rgba(23,76,60,0.55)";
    const inkSoft = dark ? "rgba(247,247,244,0.35)" : "rgba(16,16,16,0.35)";
    const mono = dark ? "rgba(247,247,244,0.30)" : "rgba(100,100,100,0.45)";

    const draw = (t) => {
      const m = mouse.current;
      m.x += (m.tx - m.x) * 0.05;
      m.y += (m.ty - m.y) * 0.05;
      const mx = m.x * w;
      const my = m.y * h;
      const scroll = window.scrollY;

      ctx.clearRect(0, 0, w, h);

      // Grid
      ctx.strokeStyle = line;
      ctx.lineWidth = 1;
      const gs = 64;
      for (let x = 0; x < w; x += gs) {
        ctx.beginPath();
        ctx.moveTo(x + ((m.x - 0.5) * -8), 0);
        ctx.lineTo(x + ((m.x - 0.5) * -8), h);
        ctx.stroke();
      }
      for (let y = 0; y < h; y += gs) {
        ctx.beginPath();
        ctx.moveTo(0, y + ((m.y - 0.5) * -8));
        ctx.lineTo(w, y + ((m.y - 0.5) * -8));
        ctx.stroke();
      }

      // Price curves bending toward cursor, layered parallax on scroll
      for (let li = 0; li < 3; li++) {
        const alpha = [0.5, 0.3, 0.16][li];
        const speed = [0.10, 0.06, 0.03][li];
        ctx.strokeStyle = dark ? `rgba(39,107,85,${alpha})` : `rgba(23,76,60,${alpha})`;
        ctx.lineWidth = li === 0 ? 1.4 : 1;
        ctx.beginPath();
        const baseY = h * (0.3 + li * 0.18) - scroll * speed;
        for (let x = 0; x <= w; x += 8) {
          const wave = Math.sin(x * 0.008 + t * 0.0004 + li * 2) * 22 + Math.sin(x * 0.02 + t * 0.0002) * 10;
          const dx = x - mx;
          const pull = Math.exp(-(dx * dx) / 40000) * (my - baseY) * 0.25;
          const y = baseY + wave + pull;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Candlestick fragments drifting slowly
      candles.forEach((c) => {
        c.y -= c.speed;
        if (c.y < -80) { c.y = h + 80; c.x = Math.random() * w; }
        const pulse = 0.25 + 0.35 * (0.5 + 0.5 * Math.sin(t * 0.0008 + c.phase));
        const py = c.y - scroll * c.layer * 0.12;
        const near = Math.max(0, 1 - Math.hypot(c.x - mx, py - my) / 260);
        const a = Math.min(0.75, pulse + near * 0.3);
        ctx.strokeStyle = c.up ? `rgba(23,76,60,${a})` : (dark ? `rgba(247,247,244,${a * 0.6})` : `rgba(16,16,16,${a * 0.5})`);
        ctx.fillStyle = c.up ? green : (dark ? `rgba(247,247,244,${a * 0.35})` : `rgba(16,16,16,${a * 0.35})`);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(c.x + c.bw / 2, py - c.bh * 0.4);
        ctx.lineTo(c.x + c.bw / 2, py + c.bh * 1.4);
        ctx.stroke();
        ctx.fillRect(c.x, py, c.bw, c.bh);
      });

      // Mono tick readouts
      ctx.font = "10px 'JetBrains Mono', monospace";
      ctx.fillStyle = mono;
      ticks.forEach((tk) => {
        const flicker = 0.5 + 0.5 * Math.sin(t * 0.0005 + tk.phase);
        ctx.globalAlpha = 0.3 + flicker * 0.5;
        ctx.fillText(tk.val, tk.x, tk.y);
        ctx.globalAlpha = 1;
      });

      raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    if (!REDUCED) {
      window.addEventListener("mousemove", onMove);
      raf = requestAnimationFrame(draw);
    } else {
      draw(0);
      cancelAnimationFrame(raf);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, [dark]);

  return <canvas ref={ref} data-testid="market-canvas" className={`absolute inset-0 ${className}`} aria-hidden="true" />;
};

export default MarketCanvas;
